/**
 * Search is ONE DOOR (audit finding 01, docs/05 § search).
 *
 * There is exactly one call: `POST /v1/search`. The Worker decides, server-side,
 * whether the query is a lookup, an AI request, or a search into a domain that is not
 * live yet. **This module contains no branch on the query text.** The user cannot be
 * expected to know whether a sentence belongs in search or in the AI tab, so the client
 * never routes — it sends the string and renders what comes back.
 *
 * `isConversational` exists in `@oprappo/contracts` so the server can apply it and the
 * client's tests can assert the client does not. It is deliberately not imported here.
 *
 * When `route: "domain_not_live"` the Worker writes a `demand_signals` row before
 * responding, and the response carries `alternatives[]` and `notify_available: true`
 * (docs/05 § errors). The client's only job there is to render a useful screen; the
 * signal is already written by the time it has anything to render.
 *
 * ── THE TRANSPORT IS THE SHARED ONE NOW, AND THAT WAS A LIVE BUG ─────────────
 * This module used to call `fetch` itself against a relative `/v1`. From the Pages
 * origin that resolves to `https://oprappo.com/v1/search` — the static site, which
 * answers 200 with `index.html`, so the one wired call in the bundle was attached to
 * the wrong host and failed as a JSON parse error far from its cause. It now goes
 * through `apps/web/src/api/client.ts`, which is the one place `VITE_API_BASE` is read
 * and the one place an error envelope becomes a typed `ApiError`.
 *
 * Sending fetch's own options is not the only thing that changed with it. The shared
 * transport attaches the access token when one is held and sends nothing when it is
 * not — which is what makes a search possible before an account exists, and what makes
 * a signed-in searcher's demand signal carry their identity rather than being written
 * anonymously by the server on their behalf.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import type {
  ErrorCode,
  SearchAlternative,
  SearchGroup,
  SearchDomainNotLive,
  SearchNotifyResponse,
  SearchPopularResponse,
  SearchRecentResponse,
  SearchRequest,
  SearchResponse,
} from '@oprappo/contracts';
import { api, currentAccessToken, isSignedOut } from '../../api/client.js';
import { readRecents } from './recent-store.js';

/**
 * `E_DOMAIN_NOT_LIVE`'s payload, as docs/05 requires it.
 *
 * The shape now lives in `@oprappo/contracts` and these are aliases of it. Three places
 * need the same object — the Worker that composes it, this client, and the fixture the
 * visual gate serves — and three declarations of one shape is the drift the contracts
 * package exists to prevent. The names are kept because the screen reads in them.
 *
 * A string may arrive EMPTY, and empty is meaningful rather than broken. The server
 * sends `''` for a sentence that does not exist — the eleventh state is drawn for one
 * of the seven unlaunched domains, and `copy_status: 'copy_missing'` names the rest —
 * and for `demand_note_bn` when the demand row was not actually written. The screen
 * renders nothing in place of an empty string; it never fills one in.
 */
export type DomainNotLiveAlternative = SearchAlternative;
export type DomainNotLiveDetail = SearchDomainNotLive;

/**
 * One card in a results group.
 *
 * ── WHY THIS IS DECLARED HERE AND WHY EVERY FIELD IS OPTIONAL ────────────────
 * `SearchGroup.items` is `unknown[]` in `@oprappo/contracts`, and deliberately: the door
 * answers from five different rungs (`apps/api/src/search/index.ts`) and the rung that
 * answers decides what an item is.
 *
 * B3 draws `রহিম ইলেকট্রিক`, `মিরপুর ১০ · ১.২ কিমি`, a যাচাইকৃত badge, `৪.৭ ★`, `২৩০ কাজ`,
 * `৩০ মিনিটে`, and a priced package below. Every one of those was once a constant in
 * `./data.ts`, rendered for EVERY query, so a search for a plumber returned an
 * air-conditioning technician with a made-up rating. Then they came from the door and the
 * door could only send a NAME — its full-text rung returned `{ offering_id, title_bn,
 * type, rank }` — so the honest screen was a nearly empty one, and the 2% fidelity gate
 * passed a blank results column at 1.98%.
 *
 * The door sends the whole card now: `apps/api/src/search/repositories.ts § FULLTEXT_SQL`
 * reads `providers` and `offering_presentation` alongside `offerings`, and
 * `0370_search_can_describe_a_card.sql` lets a signed-out visitor read all three for the
 * listings that were already public.
 *
 * The shape below is unchanged and stays the union of what the frame draws, with every
 * field optional and every field rendered ONLY when the server sent it. A field the door
 * does not send is a line that does not appear — not a dash, not a zero, not a drawn
 * default. What the door still cannot describe is in
 * `docs/bangla-needed/search-groups.md § Not a copy gap`.
 *
 * Every `_bn` field is ALREADY LOCALISED (docs/05 § conventions). `rating_bn`, `jobs_bn`
 * and `eta_bn` are strings rather than numbers for exactly that reason: `৪.৭ ★` and
 * `২৩০ কাজ` are Bangla numerals inside Bangla prose, and a client turning `4.7` into
 * `৪.৭ ★` is a client composing Bangla.
 */
export interface SearchResultItem {
  /** The offering the card describes. `offering_id` is the door's own name for it. */
  offering_id?: string;
  id?: string;
  /**
   * What the card OPENS: D1's key and the app path, composed by the door
   * (`@oprappo/contracts § SearchCard`). Absent for a listing with no page, and then the
   * card is not a button — it renders, and it does not pretend to open.
   */
  slug?: string;
  to?: string;
  /** The one field every rung supplies. A card with nothing else still has a name. */
  title_bn?: string;
  /** Second line — `মিরপুর ১০ · ১.২ কিমি` on B3's professional card. */
  meta_bn?: string;
  /** B3's square যাচাইকৃত badge. Absent means NOT VERIFIED — never a default. */
  verified_label_bn?: string | null;
  rating_bn?: string;
  jobs_bn?: string;
  eta_bn?: string;
  /** Integer minor units (paisa) — docs/05 § conventions. Absent means no price shown. */
  amount_minor?: number;
}

/**
 * A results group, as the screen reads it.
 *
 * `SearchGroup` from contracts, narrowed to the item shape above. `count_bn` is declared
 * in contracts now and the door fills it for the two types B3 draws a heading for.
 *
 * THE COUNT IS STILL RENDERED ONLY FROM `count_bn`, AND THAT DOES NOT RELAX. `count` is an
 * INTEGER and B3 draws `১২ জন` and `৩টি` — Bangla numerals inside a phrase whose counter
 * changes with what is being counted. A client turning 12 into `১২ জন` is composing Bangla
 * and picking between `জন` and `টি` is composing grammar, so the phrase is the server's
 * whole and this screen never assembles one. For the nine offering types no frame draws a
 * heading for, `count_bn` arrives empty and the heading carries no count — visibly missing
 * rather than quietly wrong. `docs/bangla-needed/search-groups.md` lists them, and the door
 * says so itself through `copy_status` / `needs`.
 */
export interface SearchResultGroup extends SearchGroup {
  items: SearchResultItem[];
  count_bn?: string | null;
}

export interface SearchResult extends SearchResponse {
  /** Narrowed from `SearchResponse['groups']`; see `SearchResultGroup`. */
  groups: SearchResultGroup[];
  domain_not_live?: DomainNotLiveDetail;
  /**
   * `E_DOMAIN_NOT_LIVE` on the not-live route, absent otherwise.
   *
   * The code travels in a 200 body because a not-live domain is a correct answer, not
   * a failed request — the one door always has one (docs/06). It is the same value
   * `states/keys.ts` maps to the drawn eleventh state, so a surface that renders states
   * from codes and this screen, which renders from `route`, reach the same frame.
   */
  code?: ErrorCode;
  /** Which rung of the server's ladder answered. Diagnostic, never rendered. */
  answered_by?: string;
  /** docs/06 § quota management. The label is the server's; the client composes none. */
  simple_mode?: boolean;
  simple_mode_label_bn?: string | null;
  /** The canonical phrase to echo, with `না, অন্য কিছু` beside it (finding 07). */
  echo_bn?: string | null;
  /** Set when a string this response needed does not exist anywhere in the repo. */
  copy_status?: 'copy_missing' | 'authored' | 'sourced';
  needs?: string[];
}

/** The one door's path. `/v1` belongs to the path, not to a base — see the transport. */
export const SEARCH_PATH = '/v1/search';

/**
 * The one door. Nothing about `q` is inspected before it is sent.
 *
 * Throws what the shared transport throws: `ApiError` for a designed failure,
 * `NetworkError` when the request never left, `UnmappedApiError` for a failure no
 * screen was drawn for. The caller decides which state to show; this decides nothing.
 */
export async function postSearch(
  body: SearchRequest,
  init?: { signal?: AbortSignal },
): Promise<SearchResult> {
  return api.post<SearchResult>(SEARCH_PATH, body, { signal: init?.signal });
}

/* ------------------------------------------------ beside the one door --- */

/** B2's সাম্প্রতিক, for a signed-in person. Their own searches; never another's. */
export const RECENT_PATH = '/v1/search/recent';
/** B2's মানুষ যা খোঁজেন. Reachable before sign-in. */
export const POPULAR_PATH = '/v1/search/popular';
/** The eleventh state's `চালু হলে জানান`. Needs an account for the notice to arrive in. */
export const NOTIFY_PATH = '/v1/search/notify';

/**
 * B2's recent searches, from wherever this person's history actually lives.
 *
 * Signed in: the Worker, which reads them from the searches the door recorded for them
 * (`GET /v1/search/recent`). Signed out: this device, which is the only place a stranger's
 * searches exist (`./recent-store.ts`). A session that turns out to have ended mid-request
 * reads the device too, because that is who is holding the phone now.
 *
 * A failure is an EMPTY list rather than an error screen: B2 is where a search starts, and
 * the rows are a convenience on it. Empty means the section is not drawn — never three
 * drawn rows standing in for a history nobody has (they used to, for everybody).
 */
export async function loadRecents(): Promise<readonly string[]> {
  if (currentAccessToken() === null) return readRecents();
  try {
    const body = await api.get<SearchRecentResponse>(RECENT_PATH);
    return body.items.map((item) => item.query_bn);
  } catch (err) {
    return isSignedOut(err) ? readRecents() : [];
  }
}

/**
 * B2's popular chips, as the Worker answers them: this week's demand, or the five B2
 * draws, and it says which (`source`). Unreachable: none — the screen does not keep a
 * copy of the five to fall back on, because a constant in the bundle presented as "what
 * people search" is the defect this replaced.
 */
export async function loadPopular(): Promise<readonly string[]> {
  try {
    const body = await api.get<SearchPopularResponse>(POPULAR_PATH);
    return body.items.map((item) => item.label_bn);
  } catch {
    return [];
  }
}

/** `চালু হলে জানান`. Throws what the transport throws; a 401 is `isSignedOut`. */
export async function postNotify(domain_id: number): Promise<SearchNotifyResponse> {
  return api.post<SearchNotifyResponse>(NOTIFY_PATH, { domain_id });
}
